import { ManagedUser, UserPartner } from '../../api/userManagement/userManagementApiTypes';
import { Partner } from '../../api/partner/partnerApiTypes';
import { AuthorizedUser, UserRole } from '../../store/user/types';

const ALL_ROLES: UserRole[] = Object.values(UserRole);

export interface RoleOption {
  label: UserRole;
  value: UserRole;
}

export const ROLE_OPTIONS: RoleOption[] = ALL_ROLES.map(role => ({ label: role, value: role }));

const ADMINISTRATOR_ROLES: UserRole[] = [UserRole.SALESCONNECT_ADMIN];

export type Status = 'loading' | 'ready';

export interface UsersFilter {
  username: string;
  name: string;
  partnerCodes: string[];
  roles: UserRole[];
}

export const initialUsersFilter: UsersFilter = {
  username: '',
  name: '',
  partnerCodes: [],
  roles: [],
};

export interface State {
  status: Status;
  filter: UsersFilter;
  users: ManagedUser[];
  savedUsers: ManagedUser[];
  partners: Partner[];
  assignableRoles: UserRole[];
}

type FilterAppliedAction = { type: 'filter/applied', payload: UsersFilter };
type UsersLoadingAction = { type: 'users/loading' };
type UsersFailedAction = { type: 'users/failed' };
type UsersLoadedAction = { type: 'users/loaded', payload: ManagedUser[] };
type PartnersLoadedAction = { type: 'partners/loaded', payload: Partner[] };
type RolesLoadedAction = { type: 'roles/loaded', payload: UserRole[] };
type UserPartnersSavedAction = { type: 'user/partnersSaved', payload: ManagedUser };
type UserRolesSavedAction = { type: 'user/rolesSaved', payload: ManagedUser };
type UserPartnersRevertedAction = { type: 'user/partnersReverted', payload: number };
type UserRolesRevertedAction = { type: 'user/rolesReverted', payload: number };
type UserPartnersSelectedAction = { type: 'user/partnersSelected', payload: { userId: number, partnerCodes: string[] } };
type UserRolesSelectedAction = { type: 'user/rolesSelected', payload: { userId: number, roles: UserRole[] } };
export type Action =
  | FilterAppliedAction
  | UsersLoadingAction
  | UsersFailedAction
  | UsersLoadedAction
  | PartnersLoadedAction
  | RolesLoadedAction
  | UserPartnersSavedAction
  | UserRolesSavedAction
  | UserPartnersRevertedAction
  | UserRolesRevertedAction
  | UserPartnersSelectedAction
  | UserRolesSelectedAction;

export const initialState: State = {
  status: 'loading',
  filter: initialUsersFilter,
  users: [],
  savedUsers: [],
  partners: [],
  assignableRoles: [],
};

export function trimUsersFilter(filter: UsersFilter): UsersFilter {
  return { ...filter, username: filter.username.trim(), name: filter.name.trim() };
}

function updateUserField<K extends keyof ManagedUser>(
  users: ManagedUser[],
  userId: number,
  field: K,
  value: ManagedUser[K],
): ManagedUser[] {
  return users.map(user => (user.id === userId ? { ...user, [field]: value } : user));
}

function mergeSavedUserField<K extends keyof ManagedUser>(
  users: ManagedUser[],
  updatedUser: ManagedUser,
  field: K,
): ManagedUser[] {
  return users.map(user => (user.id === updatedUser.id
    ? { ...user, [field]: updatedUser[field], rolesEditable: updatedUser.rolesEditable }
    : user));
}

function saveUserField<K extends keyof ManagedUser>(
  state: State,
  updatedUser: ManagedUser,
  field: K,
): State {
  return {
    ...state,
    users: mergeSavedUserField(state.users, updatedUser, field),
    savedUsers: mergeSavedUserField(state.savedUsers, updatedUser, field),
  };
}

function toUserPartners(partners: Partner[], partnerCodes: string[]): UserPartner[] {
  return partners
    .filter(partner => partnerCodes.includes(partner.code))
    .map(partner => ({
      partnerCode: partner.code,
      partnerName: partner.name,
    }));
}

function haveSameMembers(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every(member => right.includes(member));
}

function findSavedUser(state: State, userId: number): ManagedUser | undefined {
  return state.savedUsers.find(user => user.id === userId);
}

function revertUserField<K extends keyof ManagedUser>(
  state: State,
  userId: number,
  field: K,
): State {
  const savedUser = findSavedUser(state, userId);

  if (savedUser === undefined) {
    return state;
  }

  return { ...state, users: updateUserField(state.users, userId, field, savedUser[field]) };
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'filter/applied': {
      return { ...state, filter: trimUsersFilter(action.payload) };
    }
    case 'users/loading': {
      return { ...state, status: 'loading' };
    }
    case 'users/failed': {
      return {
        ...state, status: 'ready', users: [], savedUsers: [],
      };
    }
    case 'users/loaded': {
      return {
        ...state, status: 'ready', users: action.payload, savedUsers: action.payload,
      };
    }
    case 'partners/loaded': {
      return { ...state, partners: action.payload };
    }
    case 'roles/loaded': {
      return { ...state, assignableRoles: action.payload };
    }
    case 'user/partnersSaved': {
      return saveUserField(state, action.payload, 'partners');
    }
    case 'user/rolesSaved': {
      return saveUserField(state, action.payload, 'roles');
    }
    case 'user/partnersReverted': {
      return revertUserField(state, action.payload, 'partners');
    }
    case 'user/rolesReverted': {
      return revertUserField(state, action.payload, 'roles');
    }
    case 'user/partnersSelected': {
      const { userId, partnerCodes } = action.payload;
      const partners = toUserPartners(state.partners, partnerCodes);

      return { ...state, users: updateUserField(state.users, userId, 'partners', partners) };
    }
    case 'user/rolesSelected': {
      const { userId, roles } = action.payload;

      return { ...state, users: updateUserField(state.users, userId, 'roles', roles) };
    }
    default:
      return state;
  }
}

export function selectFilteredUsers(users: ManagedUser[], filter: string): ManagedUser[] {
  const lowerCasedFilter = filter.toLowerCase();

  return users.filter((user) => {
    const values = [user.id, user.username, user.name ?? ''];

    return values
      .map(value => String(value).toLowerCase())
      .some(value => value.includes(lowerCasedFilter));
  });
}

export function selectUnassignableRoles(state: State): UserRole[] {
  return ALL_ROLES.filter(role => !state.assignableRoles.includes(role));
}

function selectOwnAdministratorRoles(user: ManagedUser, administrator: AuthorizedUser): UserRole[] {
  if (user.username !== administrator.username) {
    return [];
  }

  return ADMINISTRATOR_ROLES.filter(role => administrator.roles.includes(role));
}

function selectLastRole(user: ManagedUser): UserRole[] {
  return user.roles.length === 1 ? user.roles : [];
}

export function selectLockedRoles(
  state: State,
  user: ManagedUser,
  administrator: AuthorizedUser,
): UserRole[] {
  const lockedRoles = new Set<UserRole>([
    ...selectUnassignableRoles(state),
    ...selectOwnAdministratorRoles(user, administrator),
    ...selectLastRole(user),
  ]);

  return ALL_ROLES.filter(role => lockedRoles.has(role));
}

export function selectPartnerCodes(user: ManagedUser): string[] {
  return user.partners.map(partner => partner.partnerCode);
}

export function hasPartnersChanged(state: State, user: ManagedUser): boolean {
  const savedUser = findSavedUser(state, user.id);

  return savedUser === undefined
    || !haveSameMembers(selectPartnerCodes(user), selectPartnerCodes(savedUser));
}

export function hasRolesChanged(state: State, user: ManagedUser): boolean {
  const savedUser = findSavedUser(state, user.id);

  return savedUser === undefined || !haveSameMembers(user.roles, savedUser.roles);
}

export function isLoading(state: State): boolean {
  return state.status === 'loading';
}

export function selectFilterableRoleOptions(administrator: AuthorizedUser): RoleOption[] {
  if (administrator.roles.includes(UserRole.SALESCONNECT_ADMIN)) {
    return ROLE_OPTIONS;
  }

  return ROLE_OPTIONS.filter(option => option.value !== UserRole.SALESCONNECT_ADMIN);
}
